import { isolatedDialog } from 'Addons/fitmentSearch/isolatedKeys';
import pageType from 'Addons/search/pageType';
import { replaceVehicleValue, sendInitFitmentSearchRequest } from 'Core/actions/fitmentSearch/request';
import {
  garageCleanupRequested,
  removalFromGarageRequested,
  vehicleDiscardingRequested,
  vehicleSelected,
} from 'Core/actions/fitmentSearch/vehicle';
import { sendInitHtmxRequest } from 'Core/actions/htmx';
import { assignLocation } from 'Core/actions/redirect';
import { discardQuery, replaceRequest, toggle } from 'Core/actions/request';
import type { EpicArg } from 'Core/epics/common';
import {
  createFitmentSearchFieldsSelector,
  createFitmentSearchRequestSelectionSelector,
} from 'Core/selectors/fitmentSearch/index';
import { searchLastSearchOptionsSelector, searchRequestSelector } from 'Core/selectors/search';
import type { SelectionEntry } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import uiConfig from 'Models/uiConfig/uiConfig';
import type { VehicleValue } from 'Models/vehicle';
import { getUriFromRequest } from 'Modules/converter/index';

type HtmxEventFieldValue = string | boolean | null;
type HtmxEventFields = Record<string, HtmxEventFieldValue>;

function addSelectionChanges(
  changed: HtmxEventFields,
  selection: Array<SelectionEntry | VehicleValue> | null | undefined,
) {
  selection?.forEach((sel) => {
    changed[sel.field] = sel.term;
  });
}

function addResetFields(changed: HtmxEventFields, fields: string[]) {
  fields.forEach((field) => {
    changed[field] = null;
  });
}

export default function htmxEvent({ state, action, dispatch }: EpicArg): void {
  const isolatedKey = action.meta?.isolatedKey ?? null;

  if (
    !requestConfig.isHtmxMode ||
    isolatedKey === isolatedDialog ||
    // A vehicle dropdown was changed from the isolated SearchVehicleWidget.
    (replaceVehicleValue.match(action) && action.meta.responseMeta?.isVehicleSelectionIsolated)
  ) {
    return;
  }

  const fitmentSearchRequestSelection = createFitmentSearchRequestSelectionSelector(isolatedKey)(state);
  const fitmentFields = createFitmentSearchFieldsSelector(isolatedKey)(state);
  const changed: HtmxEventFields = {};

  switch (action.type) {
    case replaceRequest.type:
      if (action.meta.goToSearchPage && uiConfig.pageType !== pageType.search) {
        const searchRequest = searchRequestSelector(state);
        const searchOptions = searchLastSearchOptionsSelector(state);
        const { href } = getUriFromRequest(searchRequest, searchOptions);

        dispatch(assignLocation(href));
        return;
      }

      if (typeof action.payload.query === 'string') {
        changed.query = action.payload.query;
      }

      addSelectionChanges(changed, action.payload.selection);
      break;

    case discardQuery.type:
      changed.query = '';
      break;

    case toggle.type: {
      const { field, term } = action.payload.value;
      changed[field] = term;
      break;
    }

    case sendInitFitmentSearchRequest.type:
      changed.isInitRequest = true;
      addSelectionChanges(changed, fitmentSearchRequestSelection);
      break;

    case sendInitHtmxRequest.type:
      // On YMM pages sendInitFitmentSearchRequest already emits the init event with the
      // fitment fields; skip here so we don't dispatch a second, YMM-field-less event.
      if (fitmentSearchConfig.needInitFitmentSearchRequest) {
        return;
      }
      changed.isInitRequest = true;
      break;

    case vehicleSelected.type:
      addSelectionChanges(changed, fitmentSearchRequestSelection);
      break;

    case replaceVehicleValue.type: {
      const { field, term } = action.payload;
      changed[field] = term;
      break;
    }

    case garageCleanupRequested.type:
    case vehicleDiscardingRequested.type:
      addResetFields(changed, fitmentFields);
      break;

    case removalFromGarageRequested.type:
      if (action.meta.wasSelected) {
        addResetFields(changed, fitmentFields);
      }
      break;

    default:
      return;
  }

  const event = new CustomEvent('cm-htmx-update', {
    detail: {
      fields: changed,
      action: action.type,
    },
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.dispatchEvent(event));
  } else {
    window.dispatchEvent(event);
  }
}
